import React, { useState } from 'react';
import Sidebar from './Sidebar';
import Header from './Header';
import './PageLayout.css';

/**
 * PageLayout Component
 * Main layout wrapper for admin pages
 *
 * @param {string} title - Page title
 * @param {string} subtitle - Page subtitle
 * @param {array} breadcrumbs - Breadcrumb items
 * @param {ReactNode} actions - Header action buttons
 * @param {ReactNode} children - Page content
 */
const PageLayout = ({
  activeView,
  onViewChange,
  title,
  subtitle,
  breadcrumbs,
  actions,
  showSearch = true,
  children,
}) => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  return (
    <div className={`oz-layout ${isSidebarCollapsed ? 'oz-layout--sidebar-collapsed' : ''}`}>
      {/* Sidebar */}
      <Sidebar
        activeView={activeView}
        onViewChange={(view) => {
          onViewChange(view);
          setIsMobileSidebarOpen(false);
        }}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={toggleSidebar}
      />

      {/* Mobile sidebar overlay */}
      {isMobileSidebarOpen && (
        <div
          className="oz-layout__sidebar-overlay"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="oz-layout__main">
        {/* Header */}
        <Header
          title={title}
          subtitle={subtitle}
          breadcrumbs={breadcrumbs}
          actions={actions}
          showSearch={showSearch}
          onMenuClick={toggleMobileSidebar}
        />

        {/* Page content */}
        <div className="oz-layout__content">
          {children}
        </div>
      </main>
    </div>
  );
};

// Page Header component for individual pages
export const PageHeader = ({
  title,
  subtitle,
  actions,
  children,
}) => {
  return (
    <div className="oz-page-header">
      <div className="oz-page-header__content">
        <div className="oz-page-header__text">
          {title && <h1 className="oz-page-header__title">{title}</h1>}
          {subtitle && <p className="oz-page-header__subtitle">{subtitle}</p>}
        </div>
        {actions && <div className="oz-page-header__actions">{actions}</div>}
      </div>
      {children}
    </div>
  );
};

// Page Content component
export const PageContent = ({ children, className = '' }) => {
  return (
    <div className={`oz-page-content ${className}`}>
      {children}
    </div>
  );
};

// Page Section component
export const PageSection = ({ title, description, actions, children, className = '' }) => {
  return (
    <section className={`oz-page-section ${className}`}>
      {(title || actions) && (
        <div className="oz-page-section__header">
          <div className="oz-page-section__text">
            {title && <h2 className="oz-page-section__title">{title}</h2>}
            {description && <p className="oz-page-section__description">{description}</p>}
          </div>
          {actions && <div className="oz-page-section__actions">{actions}</div>}
        </div>
      )}
      <div className="oz-page-section__content">
        {children}
      </div>
    </section>
  );
};

export default PageLayout;
